// useStartupChecks.ts — Vérifications automatiques au démarrage de session
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { createRappel, fetchRappels } from '../store/slices/rappelSlice';
import { encoursowner } from '../store/slices/ownerSlice';
import { fetchLeases } from '../store/slices/leaseSlice';
import { RootState } from '../store/slices/store';

export const useStartupChecks = () => {
  const dispatch = useDispatch<any>();
  const checkDone = useRef(false);

  const owner    = useSelector((state: RootState) => state.owner?.owner);
  const leases   = useSelector((state: RootState) => state.lease?.leases   || []);
  const rappels  = useSelector((state: RootState) => state.rappel?.rappels || []);
  const OwnerId  = useSelector((state: RootState) => state.authentification?.user?.OwnerId) ?? undefined;
  const token    = useSelector((state: RootState) => state.authentification?.user?.token);

  // Charge les données nécessaires au premier montage
  useEffect(() => {
    if (!token) return;
    dispatch(fetchRappels());
    dispatch(encoursowner());
    if (!leases.length) dispatch(fetchLeases());
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lance les checks une fois que tout est chargé
  useEffect(() => {
    if (!token || !owner || leases.length === 0 || checkDone.current) return;
    // Attendre que les rappels soient disponibles (peut être vide = tableau vide, c'est ok)
    checkDone.current = true;

    checkOwnerProfile();
    checkTenantAddresses();
    checkLeaseDocuments();
    checkAnnualInsurances();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, leases, rappels]);

  // ── 1. Profil propriétaire incomplet ──────────────────────────────────────
  const isEmpty = (v: any) => !v || String(v).trim() === '';

  const checkOwnerProfile = () => {
    const alreadyExists = rappels.some((r: any) => r.idtype === 1001);
    if (alreadyExists) return;

    const isIncomplete =
      isEmpty(owner?.firstname) ||
      isEmpty(owner?.lastname)  ||
      isEmpty(owner?.street)    ||
      isEmpty(owner?.city)      ||
      isEmpty(owner?.zip);

    if (isIncomplete) {
      dispatch(createRappel({
        details: 'Profil propriétaire incomplet — Nom, prénom ou adresse manquant',
        OwnerId,
        date: moment().format('YYYY-MM-DD'),
        datedebut: moment().format('YYYY-MM-DD'),
        datefin: moment().add(3, 'M').format('YYYY-MM-DD'),
        domaine: 0,
        idtype: 1001,
      }));
    }
  };

  // ── 2. Adresse locataire incomplète ───────────────────────────────────────
  const checkTenantAddresses = () => {
    leases.filter((l: any) => l.active === true).forEach((l: any) => {
      const tenant = l.tenant;
      if (!tenant) return;

      const alreadyExists = rappels.some((r: any) => r.idtype === 106 && r.LeaseId === l.id);
      if (alreadyExists) return;

      const isIncomplete =
        isEmpty(tenant.street) ||
        isEmpty(tenant.zip)    ||
        isEmpty(tenant.city)   ||
        isEmpty(tenant.phone);

      if (isIncomplete) {
        dispatch(createRappel({
          details: `Dossier locataire incomplet — ${tenant.firstname || ''} ${tenant.lastname || ''} (adresse ou téléphone manquant)`,
          OwnerId,
          date: moment().format('YYYY-MM-DD'),
          datedebut: moment().format('YYYY-MM-DD'),
          datefin: moment().add(1, 'M').format('YYYY-MM-DD'),
          domaine: 1,
          idtype: 106,
          LeaseId: l.id,
        }));
      }
    });
  };

  // ── 3. Documents bail manquants (CNI, assurance, caution) ─────────────────
  const checkLeaseDocuments = () => {
    leases.filter((l: any) => l.active === true).forEach((l: any) => {
      // CNI
      const cniExists = rappels.some((r: any) => r.idtype === 303 && r.LeaseId === l.id);
      if (!cniExists && !l.cni_uploaded) {
        dispatch(createRappel({
          details: `CNI locataire manquante — ${l.tenant?.lastname || ''}`,
          OwnerId,
          date: moment().format('YYYY-MM-DD'),
          datedebut: moment().format('YYYY-MM-DD'),
          datefin: moment().add(1, 'M').format('YYYY-MM-DD'),
          domaine: 3,
          idtype: 303,
          LeaseId: l.id,
        }));
      }

      // Assurance habitation locataire
      const assurExists = rappels.some((r: any) => r.idtype === 304 && r.LeaseId === l.id);
      if (!assurExists && !l.assurance_uploaded) {
        dispatch(createRappel({
          details: `Assurance habitation manquante — ${l.tenant?.lastname || ''}`,
          OwnerId,
          date: moment().format('YYYY-MM-DD'),
          datedebut: moment().format('YYYY-MM-DD'),
          datefin: moment().add(1, 'M').format('YYYY-MM-DD'),
          domaine: 3,
          idtype: 304,
          LeaseId: l.id,
        }));
      }

      // Caution / garant
      const cautionExists = rappels.some((r: any) => r.idtype === 305 && r.LeaseId === l.id);
      if (!cautionExists && !l.caution_uploaded) {
        dispatch(createRappel({
          details: `Caution / garant non renseigné — ${l.tenant?.lastname || ''}`,
          OwnerId,
          date: moment().format('YYYY-MM-DD'),
          datedebut: moment().format('YYYY-MM-DD'),
          datefin: moment().add(1, 'M').format('YYYY-MM-DD'),
          domaine: 3,
          idtype: 305,
          LeaseId: l.id,
        }));
      }
    });
  };

  // ── 4. Assurances annuelles (1× par an par bail) ──────────────────────────
  const checkAnnualInsurances = () => {
    const currentYear = moment().year();

    leases.filter((l: any) => l.active === true).forEach((l: any) => {
      // Assurance du bien
      const bienExists = rappels.some(
        (r: any) => r.idtype === 401 && r.LeaseId === l.id &&
          moment(r.datedebut).year() === currentYear
      );
      if (!bienExists) {
        dispatch(createRappel({
          details: `Vérifier l'assurance du bien — ${l.local?.name || l.local?.street || ''}`,
          OwnerId,
          date: moment().format('YYYY-MM-DD'),
          datedebut: moment().startOf('year').format('YYYY-MM-DD'),
          datefin: moment().endOf('year').format('YYYY-MM-DD'),
          domaine: 4,
          idtype: 401,
          LeaseId: l.id,
        }));
      }

      // Assurance habitation locataire (annuelle)
      const locExists = rappels.some(
        (r: any) => r.idtype === 402 && r.LeaseId === l.id &&
          moment(r.datedebut).year() === currentYear
      );
      if (!locExists) {
        dispatch(createRappel({
          details: `Vérifier l'assurance habitation locataire — ${l.tenant?.lastname || ''}`,
          OwnerId,
          date: moment().format('YYYY-MM-DD'),
          datedebut: moment().startOf('year').format('YYYY-MM-DD'),
          datefin: moment().endOf('year').format('YYYY-MM-DD'),
          domaine: 4,
          idtype: 402,
          LeaseId: l.id,
        }));
      }
    });
  };
};
